import useSWR from 'swr';
import { MentiError } from '@mentimeter/error-utils/error';
import { useAppSelector } from '../../../redux-hooks';
import { voteKeySelector } from '../../../selectors';
import { getUpvotes, getMigratedUpvotes } from '../../../api/results';
import type { UpvoteResults } from '../components/types';

export interface UseResultsProps {
  isMigrated: boolean;
  questionPublicKey: string;
  interactiveContentChoiceId: string | undefined;
  shouldFetch?: boolean;
}

export const useResults = <T extends UpvoteResults>({
  isMigrated,
  questionPublicKey,
  interactiveContentChoiceId,
  shouldFetch = true,
}: UseResultsProps): {
  data: T;
  error: any;
} => {
  const voteKey = useAppSelector(voteKeySelector);

  if (isMigrated && !interactiveContentChoiceId) {
    throw new MentiError('Expected interactive content choice id', {
      feature: 'slides',
      tags: {
        migrated: true,
      },
    });
  }

  const { data, error } = useSWR(
    shouldFetch ? [questionPublicKey, interactiveContentChoiceId] : null,
    async () => {
      if (isMigrated) {
        const { data } = await getMigratedUpvotes(
          voteKey,
          interactiveContentChoiceId!,
        );
        return data;
      } else {
        const { data } = await getUpvotes(voteKey, questionPublicKey);
        return data;
      }
    },
  );

  const EMPTY_DATA: UpvoteResults = { responses: [] };

  return {
    data: (data || EMPTY_DATA) as T,
    error,
  };
};
